import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { IModel, SalesforceV2CopySource } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

export const salesforceV2Adapts = {
    ...commonCopySourceAdapts,
    SOQLQuery: __IS_FABRIC_DPE__ ? undefined : '*',
    query: '*',
    includeDeletedObjects: '*',
    preserveScaleFromSchema: '*',
    partitionOption: '*',
    pageSize: GlobalVariables.pageSizeSf ? '*' : undefined
};
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SalesforceV2CopySource),
    asynchronous: true,
    adapts: salesforceV2Adapts
})
export class SalesforceV2CopySourceAdapter extends CopySourceAdapter<SalesforceV2CopySource, Model.SalesforceV2Source> {
    protected model(): IModel {
        return SalesforceV2CopySource.MODEL;
    }

    protected override async _toResource(copySource: SalesforceV2CopySource): Promise<Model.SalesforceV2Source> {
        const result: Model.SalesforceV2Source = this.getCommonCopySourceProperties(copySource, this.model().name);
        if (copySource.mode === 'query') {
            if (__IS_FABRIC_DPE__ || !copySource.isSoqlQuery) {
                result.query = ValueAdapter.toResource(copySource, SalesforceV2CopySource.READER_QUERY);
            } else {
                result.SOQLQuery = ValueAdapter.toResource(copySource, SalesforceV2CopySource.READER_QUERY);
            }
        }
        result.includeDeletedObjects = ValueAdapter.toResource(copySource, SalesforceV2CopySource.INCLUDE_DELETED);
        result.partitionOption = ValueAdapter.toResource(copySource, SalesforceV2CopySource.PARTITION_OPTION);
        result.preserveScaleFromSchema = ValueAdapter.toResource(
            copySource,
            SalesforceV2CopySource.PRESERVE_SCALE_FROM_SCHEMA
        );
        result.pageSize = GlobalVariables.pageSizeSf ? ValueAdapter.toResource(copySource, SalesforceV2CopySource.PAGESIZE) : undefined;
        return result;
    }

    protected override async _toModel(
        resource: Model.SalesforceV2Source,
        copyActivity: CopyActivity
    ): Promise<SalesforceV2CopySource> {
        const result = this.createCopySourceWithCommonProperties(
            this.model(),
            resource,
            copyActivity
        ) as SalesforceV2CopySource;
        if (resource.query) {
            ValueAdapter.toModel(result, SalesforceV2CopySource.READER_QUERY, resource.query);
            result.mode = 'query';
            result.isSoqlQuery = false;
        } else if (resource.SOQLQuery) {
            ValueAdapter.toModel(result, SalesforceV2CopySource.READER_QUERY, resource.SOQLQuery);
            result.mode = 'query';
            result.isSoqlQuery = true;
        } else {
            // salesforce v2 has 2 non-query mode, which we need to distinguish for trident
            result.mode = !!(resource.datasetSettings?.typeProperties?.reportId) ? 'report' : 'table';
        }
        if (GlobalVariables.pageSizeSf) {
            if (resource.pageSize !== undefined) {
                ValueAdapter.toModel(result, SalesforceV2CopySource.PAGESIZE, resource.pageSize);
            } else {
                delete result.pageSize;
            }
        }
        ValueAdapter.toModel(result, SalesforceV2CopySource.INCLUDE_DELETED, resource.includeDeletedObjects);
        const isPreserveScaleFromSchemaTemplated = ValueAdapter.isTemplated(
            resource,
            SalesforceV2CopySource.PRESERVE_SCALE_FROM_SCHEMA
        );
        if (isPreserveScaleFromSchemaTemplated) {
            ValueAdapter.toModel(result, SalesforceV2CopySource.PRESERVE_SCALE_FROM_SCHEMA, resource.preserveScaleFromSchema);
        } else {
            result.preserveScaleFromSchema = resource.preserveScaleFromSchema;
        }
        const isValTemplated = ValueAdapter.isTemplated(resource, SalesforceV2CopySource.PARTITION_OPTION);
        if (isValTemplated) {
            ValueAdapter.toModel(result, SalesforceV2CopySource.PARTITION_OPTION, resource.partitionOption);
        } else {
            result.partitionOption = resource.partitionOption;
        }
        return result;
    }
}
